/**
 * Voice Service Pricing Rates
 *
 * Configurable pricing table for estimating per-turn costs.
 * Defaults are based on current published rates as of May 2026.
 * These rates are used for client-side cost estimation only.
 */

/** Deepgram STT pricing — cost per second of audio processed */
export const DEEPGRAM_RATES: Record<string, number> = {
  // Nova-3 (current — May 2026)
  'nova-3': 0.0048 / 60,        // monolingual, $0.0048/min
  'nova-3-multi': 0.0058 / 60,  // multilingual, $0.0058/min

  // Legacy — kept so historical turn-usage records still calculate.
  // Runtime never selects these; engine always emits 'nova-3' or 'nova-3-multi'.
  'nova-2': 0.0059 / 60,
  enhanced: 0.0145 / 60,
  base: 0.0125 / 60,
}

/** ElevenLabs TTS pricing — cost per character (varies by plan) */
export const ELEVENLABS_RATES: Record<string, number> = {
  flash_v2_5: 0.00024, // Pro plan: ~$0.24/1K chars
  multilingual_v2: 0.00024,
}

/** OpenAI Realtime pricing — cost per token by model and direction */
export interface RealtimeTokenRates {
  textIn: number
  textOut: number
  audioIn: number
  audioOut: number
  cachedTextIn: number
  cachedAudioIn: number
}

export const OPENAI_REALTIME_RATES: Record<string, RealtimeTokenRates> = {
  'gpt-realtime': {
    textIn: 4.0 / 1_000_000,
    textOut: 16.0 / 1_000_000,
    audioIn: 32.0 / 1_000_000,
    audioOut: 64.0 / 1_000_000,
    cachedTextIn: 2.0 / 1_000_000,
    cachedAudioIn: 16.0 / 1_000_000,
  },
  'gpt-realtime-mini': {
    textIn: 0.6 / 1_000_000,
    textOut: 2.4 / 1_000_000,
    audioIn: 10.0 / 1_000_000,
    audioOut: 20.0 / 1_000_000,
    cachedTextIn: 0.3 / 1_000_000,
    cachedAudioIn: 5.0 / 1_000_000,
  },
}

/**
 * Estimate Deepgram STT cost for a turn.
 * @param model - Deepgram model name
 * @param durationMs - Audio duration in milliseconds
 */
export function estimateDeepgramCost(model: string, durationMs: number): number {
  const rate = DEEPGRAM_RATES[model] ?? DEEPGRAM_RATES['nova-3']
  return rate * (durationMs / 1000)
}

/**
 * Estimate ElevenLabs TTS cost for a turn.
 * @param model - ElevenLabs model identifier
 * @param characterCount - Number of characters sent
 */
export function estimateElevenLabsCost(
  model: string,
  characterCount: number
): number {
  const rate = ELEVENLABS_RATES[model] ?? ELEVENLABS_RATES['flash_v2_5']
  return rate * characterCount
}

/**
 * Estimate OpenAI Realtime cost from a response.done usage object.
 */
export function estimateRealtimeCost(
  model: string,
  usage: {
    textInputTokens: number
    textOutputTokens: number
    audioInputTokens: number
    audioOutputTokens: number
    cachedTextTokens?: number
    cachedAudioTokens?: number
  }
): number {
  const rates = OPENAI_REALTIME_RATES[model] ?? OPENAI_REALTIME_RATES['gpt-realtime-mini']

  const cachedText = usage.cachedTextTokens ?? 0
  const cachedAudio = usage.cachedAudioTokens ?? 0

  // Non-cached input tokens are total minus cached
  const nonCachedTextIn = Math.max(0, usage.textInputTokens - cachedText)
  const nonCachedAudioIn = Math.max(0, usage.audioInputTokens - cachedAudio)

  return (
    nonCachedTextIn * rates.textIn +
    usage.textOutputTokens * rates.textOut +
    nonCachedAudioIn * rates.audioIn +
    usage.audioOutputTokens * rates.audioOut +
    cachedText * rates.cachedTextIn +
    cachedAudio * rates.cachedAudioIn
  )
}
